import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import ButtonBase from '@mui/material/ButtonBase';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import { Tooltip } from '@katalon-studio/katalon-ui/v2';
import React, { useImperativeHandle } from 'react';
import { Account } from '../../../models';
import { useCheckPermission } from '../../../hook/useCheckPermission';
import { AccountAvatar } from '../../../components/account-avatar';

const useStyles = makeStyles(theme => ({
  accountInfo: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.75),
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    border: '1.5px solid #E6E4EF',
    padding: theme.spacing(1.75, 2),
    backgroundColor: '#fff',
    borderRadius: 12,
    transition: 'border-color .15s ease, background .15s ease',
    '&:hover': {
      borderColor: '#BFE0D3',
      backgroundColor: '#FAFCFB',
    },
    '&.Mui-focusVisible': {
      outline: '2px solid #0F8461',
      outlineOffset: 2,
    },
  },
  selected: {
    borderColor: '#0F8461',
    backgroundColor: '#F2FBF7',
    '&:hover': {
      borderColor: '#0F8461',
      backgroundColor: '#F2FBF7',
    },
  },
  name: {
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: 600,
    color: '#16141D',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  caption: {
    marginTop: 2,
    fontSize: 12.8,
    lineHeight: 1.45,
    color: '#73718A',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  check: {
    flex: 'none',
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '2px solid #CDCADF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    transition: 'all .15s ease',
  },
  checkSelected: {
    borderColor: '#0F8461',
    backgroundColor: '#0F8461',
  },
}));

const MAX_ACCOUNT_NAME_LENGTH = 40;

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const AccountInfo = React.forwardRef((
  {
    account,
    selected,
    onSelectAccount,
  } : {
    account: Account,
    selected: boolean,
    onSelectAccount: (account: Account) => void,
  },
  ref,
) => {
  const classes = useStyles();
  const { id, name, domain } = account;
  const key = `account-selection-account-info-${id}`;
  const accountDomain = domain ?? `acc-${id}`;
  const testOpsUrl = process.env.REACT_APP_TESTOPS_G3_URL?.replace('{accountDomain}', accountDomain);
  const { allowed, loading } = useCheckPermission(account);
  const handleSelectAccount = () => {
    onSelectAccount(account);
  };

  useImperativeHandle(ref, () => ({ loading, allowed }));

  const caption = account.g3 ? testOpsUrl : account.name;

  const renderAccountName = () => (
    <Box flex={1} minWidth={0}>
      <Typography component="div" className={classes.name}>
        <FormattedMessage
          id="page.account.selection.account.info.label"
          values={{
            accountId: id,
            accountName: name,
          }}
        />
      </Typography>
      {caption && (
        <Typography component="div" className={classes.caption}>
          {caption}
        </Typography>
      )}
    </Box>
  );

  const renderItem = () => (
    <ButtonBase
      key={key}
      id={key}
      role="option"
      aria-selected={selected}
      onClick={handleSelectAccount}
      className={clsx(
        classes.accountInfo,
        { [classes.selected]: selected },
      )}
    >
      <AccountAvatar
        id={`account-selection-account-avatar-${id}`}
        account={account}
        size={40}
      />
      {account.name.length > MAX_ACCOUNT_NAME_LENGTH
        ? (
          <Tooltip
            key={`${key}-tooltip`}
            title={account.name}
            placement="top-start"
          >
            {renderAccountName()}
          </Tooltip>
        )
        : renderAccountName()}
      <Box className={clsx(classes.check, { [classes.checkSelected]: selected })}>
        {selected && <CheckIcon />}
      </Box>
    </ButtonBase>
  );

  return allowed ? renderItem() : null;
});
